import { useEffect, useState } from "react";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import SortIcon from "../ui-icons/table/SortIcon";
import { urlDefault } from "src/constants";
import { siteUrlOrSiteApi } from "src/api/config/systemSwitch";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectUsers } from "src/modules/users/selector";
import { EmptyList } from "../EmptyList";
import { addUsers } from "src/modules/users/actions";

function safeGetUserEmail(user: any): string | null {
  if (!user) return null;
  if (Array.isArray(user)) {
    for (const u of user) {
      const v = safeGetUserEmail(u);
      if (v) return v;
    }
    return null;
  }
  // SharePoint FieldUserValue
  if (typeof user.get_email === "function") {
    try {
      const v = user.get_email();
      if (typeof v === "string" && v.includes("@")) return v;
      return null;
    } catch {
      return null;
    }
  }
  // Common plain-object shapes
  const candidate =
    user.email ||
    user.Email ||
    user.EMail ||
    user.value ||
    user.accountName ||
    user.AccountName ||
    user.name ||
    user.Name ||
    null;

  if (typeof candidate !== "string") return null;
  const normalized = normalizeEmailLike(candidate);
  return normalized.includes("@") ? normalized : null;
}

function normalizeEmailLike(raw: string): string {
  let s = raw.trim();
  // AccountName / claims format: i:0e.t|...|email@domain
  if (s.includes("|")) s = s.split("|")[s.split("|").length - 1] || s;
  // Sometimes users are marked as deleted
  if (s.includes("-deleted")) s = s.split("-deleted")[0] || s;
  // Strip mailto:
  if (s.toLowerCase().startsWith("mailto:")) s = s.slice("mailto:".length);
  return s.trim();
}

function safeGetEmailFromAny(value: any): string | null {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const v of value) {
      const email = safeGetEmailFromAny(v);
      if (email) return email;
    }
    return null;
  }
  if (typeof value === "string") {
    const e = normalizeEmailLike(value);
    return e.includes("@") ? e : null;
  }
  return safeGetUserEmail(value);
}

function safeUserNameByEmail(users: any, email: string | null): string | null {
  if (!email) return null;
  const key = email.toLowerCase();
  const u = users?.[key];
  return (u && (u.displayName || u.fullName)) || null;
}

function safeUserNameFromRecord(record: any): string | null {
  if (!record) return null;
  if (Array.isArray(record)) {
    for (const r of record) {
      const name = safeUserNameFromRecord(r);
      if (name) return name;
    }
    return null;
  }
  if (typeof record.get_lookupValue === "function") {
    try {
      const v = record.get_lookupValue();
      if (typeof v === "string" && v.trim()) {
        const s = v.trim();
        // Avoid showing IDs/personnel numbers as names
        if (/^\d+$/.test(s)) return null;
        if (!/[A-Za-zА-Яа-яЁё]/.test(s)) return null;
        return s;
      }
    } catch {
      // ignore
    }
  }
  const v: any = record.Name || record.name || record.Title || record.title || null;
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return null;
  if (!/[A-Za-zА-Яа-яЁё]/.test(s)) return null;
  return s;
}

function safeGetObserverEmail(item: any): string | null {
  return (
    safeGetEmailFromAny(item?.ObserverEmail) ||
    safeGetEmailFromAny(item?.Observer) ||
    safeGetEmailFromAny(item?.ObserverString) ||
    null
  );
}

function safeGetObservedEmail(item: any): string | null {
  return (
    safeGetEmailFromAny(item?.ObservedUserEmail) ||
    safeGetEmailFromAny(item?.ObservedUser) ||
    null
  );
}

const MyTasksAuditsTable = (props: any) => {
  const { data, setExportData, customLoading } = props;
  const [sortData, setSortData] = useState<any>([]);
  const [query, setQuery] = useSearchParams();
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();

  // Proactively preload user profiles so the list renders Russian names (PreferredNameRus) without opening the card.
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;
    const emails: string[] = [];
    data.forEach((item: any) => {
      const observer = safeGetObserverEmail(item);
      const observed = safeGetObservedEmail(item);
      if (observer) emails.push(observer.toLowerCase());
      if (observed) emails.push(observed.toLowerCase());
    });
    const uniq = Array.from(new Set(emails)).filter(Boolean);
    const missing = uniq.filter((e) => !Object.prototype.hasOwnProperty.call(users || {}, e));
    if (missing.length > 0) dispatch(addUsers(missing as any));
  }, [dispatch, data, users]);

  const [tableHeaderContent, setTableHeaderContent] = useState<any>({
    DateOb: {
      title: "Дата наблюдения",
      sort: true,
      style: { width: "11.7%" },
      sortDirection: "default",
      sortType: "date",
    },
    ObserverStr: {
      title: "ФИО наблюдателя",
      sort: true,
      style: { width: "12.84%" },
      sortDirection: "default",
      sortType: "string",
    },
    ObservedStr: {
      title: "ФИО наблюдаемого",
      sort: true,
      style: { width: "12.61%" },
      sortDirection: "default",
      sortType: "string",
    },
    LocationStr: {
      title: "Локация",
      sort: true,
      style: { width: "8.13%" },
      sortDirection: "default",
      sortType: "string",
    },
    DepartmentStr: {
      title: "Отдел",
      sort: true,
      style: { width: "12.23%" },
      sortDirection: "default",
      sortType: "string",
    },
    RegionStr: {
      title: "Участок",
      sort: true,
      style: { width: "6.38%" },
      sortDirection: "default",
      sortType: "string",
    },
    actionsPlanStr: {
      title: "Действия запланированы",
      sort: true,
      style: { width: "10.41%" },
      sortDirection: "default",
      sortType: "string",
    },
    actionsClosedStr: {
      title: "Действия закрыты",
      sort: true,
      style: { width: "7.22%" },
      sortDirection: "default",
      sortType: "string",
    },
    AGRB: {
      title: "Нарушение связано с золотым правилом безопасности",
      sort: true,
      style: { width: "18.47%" },
      sortDirection: "default",
      sortType: "string",
    },
  });
  const [tableHeaderContentExel] = useState<any>({
    ID: {
      title: "ID Поведенческого аудита",
      width: 50,
    },
    Location: {
      title: "Локация",
      width: 50,
    },
    Department: {
      title: "Отдел",
      width: 50,
    },
    Region: {
      title: "Участок",
      width: 50,
    },
    DateOb: {
      title: "Дата наблюдения",
      width: 50,
    },
    Observer: {
      title: "ФИО наблюдателя",
      width: 50,
    },
    Observed: {
      title: "ФИО наблюдаемого",
      width: 50,
    },
    AFL: {
      title: "Аудит проводился совместно с членом команды Front Line?",
      width: 50,
    },
    A5S: {
      title: "Соответствует ли рабочая зона стандартам 5S?",
      width: 50,
    },
    AGRB: {
      title: "Нарушение связано с золотым правилом безопасности",
      width: 50,
    },
    AGR: {
      title: "Золотое правило",
      width: 50,
    },
    AR: {
      title: "Знание рисков",
      width: 50,
    },
    AS: {
      title: "Применение SAFE",
      width: 50,
    },
    AP: {
      title: "Человеческий фактор (состояния)",
      width: 50,
    },
    APC: {
      title: "Человеческий фактор (критические ошибки)",
      width: 50,
    },
    ASN: {
      title:
        "Знает ли сотрудник правила безопасности при выполнении текущей работы?",
      width: 50,
    },
    AFB: {
      title: "Обратная связь аудитора",
      width: 50,
    },
    AFBS: {
      title: "Принял ли сотрудник обратную связь от аудитора?",
      width: 50,
    },
    AUPG: {
      title: "Основные области для улучшения",
      width: 50,
    },
    Description: {
      title: "Описание",
      width: 50,
    },
    Feedback: {
      title: "Обратная связь от человека, за которым наблюдали",
      width: 50,
    },
    actionsPlan: {
      title: "Действия запланированы",
      width: 50,
    },
    IDS_Actions: {
      title: "ID Действий",
      width: 50,
    },
    Actions: {
      title: "Действия",
      width: 50,
    },
  });

  const openDocumentCard = (id: number) => {
    const newSearchParams = new URLSearchParams(query);
    newSearchParams.set("auditCardMobile", id.toString());
    setQuery(newSearchParams);
  };

  const sortDataHandle = (
    sortField: string,
    sortDirection: string,
    sortType: string
  ) => {
    const tempTableHeaderContent = { ...tableHeaderContent };
    const tempData = [
      ...data.map((item: any) => {
        const observedEmail = safeGetObservedEmail(item) || "";
        const observerEmail = safeGetObserverEmail(item) || "";
        return {
          ...item,
          ...{
            actionsPlanStr: (item.actionsPlan && "Да") || "Нет",
            actionsClosedStr: (item.actionsClosed && "Да") || "Нет",
            ObservedStr:
              (item.ObservedType === "Внутренний" &&
                (safeUserNameByEmail(users, observedEmail) || safeUserNameFromRecord(item.ObservedUser))) ||
              item.ObservedString ||
              "-",
            RegionStr: item.Region.get_lookupValue(),
            DepartmentStr: item.Department.get_lookupValue(),
            LocationStr: item.Location.get_lookupValue(),
            ObserverStr:
              (safeUserNameByEmail(users, observerEmail) ||
                safeUserNameFromRecord(item.Observer) ||
                safeUserNameFromRecord(item.ObserverString)) ||
              "-",
          },
        };
      }),
    ];
    Object.keys(tempTableHeaderContent).forEach((key: string) => {
      tempTableHeaderContent[key]["sortDirection"] = "default";
    });
    if (sortDirection === "default") {
      tempTableHeaderContent[sortField]["sortDirection"] = "desc";
    }
    if (sortDirection === "desc") {
      tempTableHeaderContent[sortField]["sortDirection"] = "asc";
    }
    if (sortDirection === "asc") {
      tempTableHeaderContent[sortField]["sortDirection"] = "default";
      setSortData([...data]);
    }
    if (sortType === "number") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        tempData.sort(
          (a: any, b: any) =>
            parseFloat(a[sortField]) - parseFloat(b[sortField])
        );
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        tempData.sort(
          (a: any, b: any) =>
            parseFloat(b[sortField]) - parseFloat(a[sortField])
        );
      }
    }
    if (sortType === "string") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        tempData.sort((a: any, b: any) => {
          return a[sortField].localeCompare(b[sortField]);
        });
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        tempData.sort((a: any, b: any) => {
          return b[sortField].localeCompare(a[sortField]);
        });
      }
    }
    if (sortType === "date") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        const distantFuture = new Date(8640000000000000);
        tempData.sort((a: any, b: any) => {
          let dateA = a[sortField] || distantFuture;
          let dateB = b[sortField] || distantFuture;
          return dateA.getTime() - dateB.getTime();
        });
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        const distantFuture = new Date(8640000000000000);
        tempData.sort((a: any, b: any) => {
          let dateA = a[sortField] || distantFuture;
          let dateB = b[sortField] || distantFuture;
          return dateB.getTime() - dateA.getTime();
        });
      }
    }
    setTableHeaderContent({ ...tempTableHeaderContent });
    setSortData([...tempData]);
  };

  useEffect(() => {
    setSortData([...data]);
    const tempTableHeaderContent = { ...tableHeaderContent };
    Object.keys(tempTableHeaderContent).forEach((key: string) => {
      tempTableHeaderContent[key]["sortDirection"] = "default";
    });
    setTableHeaderContent({ ...tempTableHeaderContent });
  }, [data]);

  useEffect(() => {
    if (sortData.length > 0) {
      let exportColumns: any = [];
      Object.keys(tableHeaderContentExel).forEach((key: any) => {
        exportColumns = [
          ...exportColumns,
          {
            header: tableHeaderContentExel[key]["title"],
            key: key,
            width: tableHeaderContentExel[key]["width"],
          },
        ];
      });
      let exportData: any = [];
      sortData.forEach((item: any) => {
        const observedEmail = safeGetObservedEmail(item) || "";
        const observerEmail = safeGetObserverEmail(item) || "";
        exportData = [
          ...exportData,
          {
            ID: {
              text: item.ID,
              hyperlink: `${siteUrlOrSiteApi}${urlDefault}/dashboard/3/behavioral-audit?auditCardMobile=${item.ID}`,
              tooltip: item.Title,
            },
            Location: item.Location.get_lookupValue(),
            Department: item.Department.get_lookupValue(),
            Region: item.Region.get_lookupValue(),
            DateOb: moment(item.DateOb).format("DD.MM.YYYY"),
            Observer:
              (safeUserNameByEmail(users, observerEmail) || safeUserNameFromRecord(item.Observer)) ||
              "-",
            Observed:
              (item.ObservedType === "Внутренний" &&
                (safeUserNameByEmail(users, observedEmail) || safeUserNameFromRecord(item.ObservedUser))) ||
              item.ObservedString ||
              "-",
            AFL: item.AFL,
            A5S: item.A5S,
            AGRB: item.AGRB,
            AGR: (item.AGRB === "Да" && item.AGR.join(`\u000A`)) || "-",
            AR: item.AR,
            AS: item.AS,
            AP: (item.AP && item.AP.join(`\u000A`)) || "-",
            APC: (item.APC && item.APC.join(`\u000A`)) || "-",
            ASN: item.ASN,
            AFB: item.AFB,
            AFBS: item.AFBS,
            AUPG: (item.AUPG && item.AUPG.join(`\u000A`)) || "-",
            Description: item.Description,
            Feedback: item.Feedback,
            actionsPlan:
              (typeof item.actionsPlan == "boolean" &&
                item.actionsPlan &&
                "Да") ||
              "Нет" ||
              item.actionsPlan,
            IDS_Actions:
              (item.actions.length > 0 &&
                item.actions.map((action: any) => action.ID).join(`\u000A`)) ||
              "-",
            Actions:
              (item.actions.length > 0 &&
                item.actions
                  .map((action: any) => `- ${action.Description}`)
                  .join(`\u000A`)) ||
              "-",
          },
        ];
      });
      setExportData({ columns: [...exportColumns], data: [...exportData] });
    }
  }, [sortData, users]);

  return (
    <FlexCol className="w-full">
      <FlexCol className="relative">
        <FlexCol
          id="owd_calendar_wrapper"
          className="relative pb-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom no-scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-210px)] min-h-[170px]"
        >
          <FlexRow className="pb-5 sticky-custom max-h-[65px] w-full bg-white pt-5 px-10 gap-5 min-w-[1316px] z-10 hidden">
            {Object.keys(tableHeaderContent).map((fieldKey) => {
              const key: string = fieldKey;
              return (
                <TableHeaderColumn
                  key={fieldKey}
                  sortField={fieldKey}
                  tableHeaderContent={tableHeaderContent[key]}
                  sortDataHandle={sortDataHandle}
                />
              );
            })}
          </FlexRow>
          <FlexCol className="divide-y grow w-full px-[8px]">
            {sortData.map((item: any) => (
              <FlexCol
                className="py-4 -mx-2 px-[16px] gap-[8px] cursor-pointer text-[#637381] hover:bg-[#F3F4F6]"
                key={item.ID}
                onClick={() => openDocumentCard(item.ID)}
              >
                <FlexCol style={{ width: "100%" }} className="break-words text-[13px] text-[#012169]">
                    Наблюдатель:
                  <span className="block text-sm font-normal not-italic">
                    {(() => {
                      const email = safeGetObserverEmail(item);
                      return (
                        safeUserNameByEmail(users, email) ||
                        safeUserNameFromRecord(item.Observer) ||
                        safeUserNameFromRecord(item.ObserverString) ||
                        "-"
                      );
                    })()}
                  </span>
                </FlexCol>
                <FlexCol style={{ width: "100%" }} className="break-words text-[13px] text-[#012169]">
                    Наблюдаемый:
                  <span className="block text-sm font-normal not-italic">
                    {(() => {
                      if (item.ObservedType === "Внутренний") {
                        const email = safeGetObservedEmail(item);
                        return (
                          safeUserNameByEmail(users, email) ||
                          safeUserNameFromRecord(item.ObservedUser) ||
                          item.ObservedString ||
                          "-"
                        );
                      }
                      return item.ObservedString || "-";
                    })()}
                  </span>
                </FlexCol>
                <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic text-[13px]">
                    {moment(item.DateOb).format("DD.MM.YYYY")}
                  </span>
                </FlexCol>
                <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic text-[13px]">
                    {item.Location.get_lookupValue()}/{item.Department.get_lookupValue()}/{item.Region.get_lookupValue()}
                  </span>
                </FlexCol>
                {/* <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic">
                    {item.Department.get_lookupValue()}
                  </span>
                </FlexCol> */}
                {/* <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic">
                    {item.Region.get_lookupValue()}
                  </span>
                </FlexCol> */}
                <FlexRow style={{ width: "100%" }} className="break-words gap-[5px] text-[13px]">
                    Действия запланированы:
                  <span className="block text-sm font-normal not-italic">
                    {(item.actionsPlan && "Да") || "Нет"}
                  </span>
                </FlexRow>
                {/* <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic">
                    {(item.actionsClosed && "Да") || "Нет"}
                  </span>
                </FlexCol> */}
                {/* <FlexCol style={{ width: "100%" }} className="break-words">
                  <span className="block text-sm font-normal not-italic">
                    {item.AGRB}
                  </span>
                </FlexCol> */}
              </FlexCol>
            ))}
          </FlexCol>
          {!customLoading && data.length === 0 && (
            <FlexCol className="justify-center items-center absolute pt-[60px] top-0 left-0 w-full h-full items-center justify-center min-h-[170px] max-h-[calc(100vh-210px)]">
              <EmptyList title="Данные не найдены" />
            </FlexCol>
          )}
        </FlexCol>
        {/* {!customLoading && data.length > 0 && (
          <div className="w-full my-2 py-2 text-center bg-[#012169] text-white">
            Список сформирован
          </div>
        )}
        {customLoading && (
          <div className="w-full my-2 py-2 text-center animate-pulse">
            Формируется список, пожалуйста подождите
          </div>
        )} */}
      </FlexCol>
    </FlexCol>
  );
};

export default MyTasksAuditsTable;

export function TableHeaderColumn({
  tableHeaderContent,
  sortDataHandle,
  sortField,
}: any) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2">
        {tableHeaderContent.icon && (
          <FlexCol>{tableHeaderContent.icon}</FlexCol>
        )}
        <FlexCol className="font-medium whitespace-pre-line w-fit">
          {tableHeaderContent.title}
        </FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol
            className="w-auto cursor-pointer"
            onClick={() =>
              sortDataHandle(
                sortField,
                tableHeaderContent.sortDirection,
                tableHeaderContent.sortType
              )
            }
          >
            <SortIcon sortDirection={tableHeaderContent.sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
